/**
 * Per-project task counts, read off the document.
 *
 * **The overdue rule is `entries/task/`'s, and this domain calls it.** `isProjectOverdue` sits in
 * `entries/task/predicates.ts` beside `isDueTodayCandidate` and `isUpcomingCandidate` (#392), on
 * #154's measurement: three incompatible task predicates named side by side, where the divergence
 * is visible. A predicate over a task is task vocabulary. This module used to inline the rule as an
 * anonymous expression, which is what let it drift from `formatDueDate` (`utils/date.ts`) unnoticed
 * — that one derives "overdue" from a day difference, this one compares a `YYYY-MM-DD` string
 * lexicographically. Both still exist; the shared name is what makes the disagreement visible
 * (#148).
 *
 * `today` is a parameter rather than a `new Date()` inside, so the rule is testable without a
 * clock. It must come from `formatDateString`, never `toISOString`.
 */

import { useMemo } from 'react'
import { useDocumentStatus, useDocumentView } from '@/document'
import type { DocumentView } from '@/document'
import { isProjectOverdue } from '@/entries'
import { formatDateString } from '@/lib/date'

export interface ProjectTaskCounts {
  total: number
  completed: number
  percentage: number
  overdue: number
}

export type ProjectStats = Record<string, ProjectTaskCounts>

/**
 * Counts keyed by project id. A project with no live tasks has no key.
 */
export function selectProjectStats(
  view: DocumentView | null,
  today: string,
): ProjectStats {
  if (!view) return {}

  const stats: ProjectStats = {}

  for (const entry of Object.values(view.entries)) {
    if (entry.type !== 'task' || !entry.projectId) continue
    if (entry.archivedAt) continue

    const pid = entry.projectId
    if (!stats[pid]) {
      stats[pid] = { total: 0, completed: 0, percentage: 0, overdue: 0 }
    }

    stats[pid].total += 1

    if (entry.metadata?.status === 'done') {
      stats[pid].completed += 1
    } else if (isProjectOverdue(entry, today)) {
      stats[pid].overdue += 1
    }
  }

  for (const counts of Object.values(stats)) {
    counts.percentage =
      counts.total > 0 ? Math.round((counts.completed / counts.total) * 100) : 0
  }

  return stats
}

/** `selectProjectStats` bound to this device's document, against today. */
export function useProjectStats(): {
  data: ProjectStats
  isLoading: boolean
} {
  const doc = useDocumentView()
  const ready = useDocumentStatus()

  const data = useMemo(
    () => selectProjectStats(doc, formatDateString(new Date())),
    [doc],
  )

  return { data, isLoading: !ready }
}
